import { createFileRoute, redirect } from '@tanstack/react-router'
import {
  BOT_DETECTION_PREDEFINED_DATE_RANGE_IDS,
  BOT_DETECTION_TIMELINE_CHART_IDS,
  botDetectionArraySearchParam,
  botDetectionBooleanSearchParam,
  botDetectionCategorySearchParamSchema,
  botDetectionConfidenceSearchParamSchema,
  botDetectionIdentitySearchParamSchema,
  BotDetectionOverviewPage,
  botDetectionSourceSearchParamSchema,
} from 'features/botDetection'
import { withCachedSearch } from 'helpers/withCachedSearch'
import { z } from 'zod/v4'

const botDetectionOverviewSearchSchema = z.object({
  since: z.string().optional(),
  before: z.string().optional(),
  period: z.enum(BOT_DETECTION_PREDEFINED_DATE_RANGE_IDS).optional().catch(undefined),
  category: botDetectionArraySearchParam(botDetectionCategorySearchParamSchema),
  botName: botDetectionArraySearchParam(z.string()),
  identity: botDetectionArraySearchParam(botDetectionIdentitySearchParamSchema),
  confidence: botDetectionArraySearchParam(botDetectionConfidenceSearchParamSchema),
  provider: botDetectionArraySearchParam(z.string()),
  proxy: botDetectionBooleanSearchParam,
  vpn: botDetectionBooleanSearchParam,
  environmentIds: botDetectionArraySearchParam(z.string()),
  source: botDetectionSourceSearchParamSchema.optional().catch(undefined),
  selectedChart: z.enum(BOT_DETECTION_TIMELINE_CHART_IDS).optional(),
  timelineGranularity: z.enum(['hour', 'day', 'month']).optional(),
})

const botDetectionCachedSearchParams = [
  'period',
  'before',
  'since',
  'category',
  'botName',
  'provider',
  'identity',
  'confidence',
  'proxy',
  'vpn',
  'environmentIds',
  'selectedChart',
  'timelineGranularity',
] as const

export type BotDetectionOverviewSearchParams = z.infer<typeof botDetectionOverviewSearchSchema>
type BotDetectionCachedSearchParam = (typeof botDetectionCachedSearchParams)[number]
type BotDetectionCachedSearch = Pick<BotDetectionOverviewSearchParams, BotDetectionCachedSearchParam>

const getBotDetectionCachedSearchRoute = (subscriptionId: string) => `/workspaces/${subscriptionId}/bot-detection`

export const Route = createFileRoute('/_authenticated/_layout/workspaces/$subscriptionId/bot-detection/')({
  validateSearch: botDetectionOverviewSearchSchema,
  component: BotDetectionOverviewPage,
  beforeLoad: ({ search, location, params, cause }) => {
    if (cause === 'preload') {
      return
    }

    const cachedSearch = withCachedSearch<BotDetectionCachedSearch>({
      search,
      matchedRoute: getBotDetectionCachedSearchRoute(params.subscriptionId),
      cachedParamsWhitelist: botDetectionCachedSearchParams,
      searchStr: location.searchStr,
    })

    if (cachedSearch) {
      throw redirect({
        to: '/workspaces/$subscriptionId/bot-detection',
        params,
        replace: true,
        search: (old) => ({
          ...old,
          ...cachedSearch,
        }),
      })
    }
  },
})
